import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { afterPayoutSetup } from '@/lib/setupSteps'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react'
import athleteApi from '../lib/api'

type StripeStatus = 'loading' | 'complete' | 'pending' | 'error'

const StripeReturn = () => {
  const navigate = useNavigate()
  const { athlete } = useAuth()
  const [status, setStatus] = useState<StripeStatus>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const response = await athleteApi.get('/athlete/stripe/onboarding-return')
        const data = response.data

        if (data.payouts_enabled) {
          setStatus('complete')
          setMessage(
            'Your bank account has been successfully connected. You can now receive payouts!',
          )
        } else if (data.details_submitted) {
          setStatus('pending')
          setMessage(
            'Your account details have been submitted and are being verified. This usually takes 1-2 business days.',
          )
        } else {
          setStatus('pending')
          setMessage('Your account setup is incomplete. Please complete all required steps.')
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error)
        setStatus('error')
        setMessage('There was an error checking your account status. Please try again.')
      }
    }

    checkOnboardingStatus()
  }, [])

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
      case 'complete':
        return <CheckCircle className="h-12 w-12 text-green-600" />
      case 'pending':
        return <Clock className="h-12 w-12 text-yellow-600" />
      case 'error':
        return <AlertCircle className="h-12 w-12 text-red-600" />
    }
  }

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Checking your account...'
      case 'complete':
        return 'Account Connected!'
      case 'pending':
        return 'Almost There!'
      case 'error':
        return 'Something went wrong'
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'bg-blue-500/20 border-blue-500/30'
      case 'complete':
        return 'bg-green-500/20 border-green-500/30'
      case 'pending':
        return 'bg-yellow-500/20 border-yellow-500/30'
      case 'error':
        return 'bg-red-500/20 border-red-500/30'
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card variant="dark" className="max-w-md w-full overflow-hidden">
        <CardHeader className="text-center pb-4">
          <div className={`mx-auto mb-4 p-6 rounded-full ${getStatusColor()}`}>
            {getStatusIcon()}
          </div>
          <CardTitle className="text-foreground text-xl mb-2">{getStatusTitle()}</CardTitle>
          <CardDescription className="text-gray-600">{message}</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <button
            onClick={() => navigate(afterPayoutSetup(athlete))}
            className="w-full px-6 py-3 bg-[#C0FD72] text-black rounded-md hover:bg-[#d2ff9c] font-medium"
          >
            {status === 'complete'
                ? 'Go to Earnings'
                : 'Back to Earnings'}
          </button>
        </CardContent>
      </Card>
    </div>
  )
}

export default StripeReturn
